import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  Calendar, FileText, TrendingUp, Sun, Sunset, Moon,
  AlertCircle, Clock, CheckCircle2, Coffee, BookOpen, MapPin,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { PortalLayout } from "@/components/portal-layout";
import { CountUp } from "@/components/animations";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useDisplayInfo } from "@/lib/auth";

export const Route = createFileRoute("/student/")({ component: StudentHome });

type TodayClass = { name: string; info: string };
type DueItem = {
  Icon: React.ComponentType<{ className?: string }>;
  color: string;
  bg: string;
  title: string;
  time: string;
};

function StudentHome() {
  const { user } = useAuth();
  const display = useDisplayInfo();
  const [todayClasses, setTodayClasses] = React.useState<TodayClass[]>([]);
  const [attendancePct, setAttendancePct] = React.useState(0);
  const [completionPct, setCompletionPct] = React.useState(0);
  const [avgScore, setAvgScore] = React.useState(0);
  const [due, setDue] = React.useState<DueItem[]>([]);

  React.useEffect(() => {
    if (!user) return;
    (async () => {
      const today = new Date();
      const todayStart = new Date(today); todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(today); todayEnd.setHours(23, 59, 59, 999);

      const { data: enrolls } = await supabase
        .from("enrollments").select("class_id").eq("student_id", user.id).eq("is_active", true);
      const classIds = (enrolls ?? []).map(e => e.class_id);
      if (!classIds.length) return;

      const [classesRes, sessRes, attRes, assignRes, subsRes] = await Promise.all([
        supabase.from("classes").select("id, name, subject").in("id", classIds),
        supabase.from("class_sessions")
          .select("id, starts_at, room, class_id")
          .in("class_id", classIds)
          .gte("starts_at", todayStart.toISOString())
          .lte("starts_at", todayEnd.toISOString())
          .order("starts_at"),
        supabase.from("attendances").select("status, student_id").eq("student_id", user.id),
        supabase.from("assignments").select("id, title, due_at, class_id").in("class_id", classIds),
        supabase.from("submissions").select("assignment_id, score").eq("student_id", user.id),
      ]);

      const classMap = new Map((classesRes.data ?? []).map(c => [c.id, c]));

      setTodayClasses((sessRes.data ?? []).map(s => {
        const c = classMap.get(s.class_id);
        return {
          name: c?.name ?? "Lớp học",
          info: `${format(new Date(s.starts_at), "HH:mm")} · Phòng ${s.room ?? "—"}`,
        };
      }));

      const att = attRes.data ?? [];
      const present = att.filter(a => a.status === "present" || a.status === "late").length;
      setAttendancePct(att.length ? Math.round((present / att.length) * 100) : 0);

      const assigns = assignRes.data ?? [];
      const subs = subsRes.data ?? [];
      const subMap = new Map(subs.map(s => [s.assignment_id, s]));
      const completed = assigns.filter(a => subMap.has(a.id)).length;
      setCompletionPct(assigns.length ? Math.round((completed / assigns.length) * 100) : 0);

      const scored = subs.filter(s => s.score !== null);
      setAvgScore(scored.length ? +(scored.reduce((a, b) => a + Number(b.score), 0) / scored.length).toFixed(1) : 0);

      const upcoming = assigns
        .filter(a => a.due_at && !subMap.has(a.id))
        .map(a => {
          const d = new Date(a.due_at!);
          const days = differenceInDays(d, today);
          const cfg = days < 0
            ? { Icon: AlertCircle, color: "text-red-600", bg: "bg-red-50 dark:bg-red-500/10" }
            : days <= 0
            ? { Icon: Clock, color: "text-rose-600", bg: "bg-rose-50 dark:bg-rose-500/10" }
            : days <= 2
            ? { Icon: Clock, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-500/10" }
            : { Icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50 dark:bg-emerald-500/10" };
          const time = days < 0 ? `Quá hạn ${-days} ngày` : days === 0 ? "Hết hạn hôm nay" : `Còn ${days} ngày`;
          return { ...cfg, title: a.title, time, _days: days };
        })
        .sort((a, b) => a._days - b._days)
        .slice(0, 4)
        .map(({ _days, ...rest }) => rest);
      setDue(upcoming);
    })();
  }, [user]);

  const firstName = (display.name || "Học sinh").split(" ").slice(-1)[0];
  const hour = new Date().getHours();
  const greeting = hour < 11 ? "Chào buổi sáng," : hour < 18 ? "Chào buổi chiều," : "Chào buổi tối,";
  const GreetIcon = hour < 11 ? Sun : hour < 18 ? Sunset : Moon;

  return (
    <PortalLayout portal="student" breadcrumb={[{ label: "Học sinh" }, { label: "Trang chủ" }]}>
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl p-8 mb-6 text-white"
        style={{ background: "linear-gradient(135deg, #92400E, #FB923C)" }}
      >
        <div className="absolute inset-0 bg-mesh opacity-40" />
        <div className="relative">
          <div className="text-sm opacity-90 mb-2 flex items-center">
            <GreetIcon className="size-5 inline mr-1.5 opacity-80" />
            {greeting}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold mb-3">{firstName}!</h1>
          <p className="opacity-90 mb-6">
            {todayClasses.length ? `Hôm nay bạn có ${todayClasses.length} lớp học` : "Không có lớp hôm nay"}
          </p>
          {todayClasses.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
              {todayClasses.slice(0, 4).map((c, i) => (
                <div key={i} className="p-4 rounded-2xl bg-white/15 backdrop-blur">
                  <div className="font-semibold flex items-center gap-2">
                    <BookOpen className="size-4 shrink-0 opacity-90" />
                    <span className="truncate">{c.name}</span>
                  </div>
                  <div className="text-xs opacity-90 mt-1 flex items-center gap-1">
                    <MapPin className="size-3" /> {c.info}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 rounded-2xl bg-white/15 backdrop-blur text-center max-w-md">
              <Coffee className="size-8 mx-auto mb-2 opacity-80" />
              <div className="font-medium">Hôm nay bạn không có lớp nào</div>
              <div className="text-sm opacity-80 mt-1">Nghỉ ngơi hoặc ôn bài nhé!</div>
            </div>
          )}
        </div>
      </motion.div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Điểm danh", value: attendancePct, suffix: "%", icon: Calendar },
          { label: "Bài tập hoàn thành", value: completionPct, suffix: "%", icon: FileText },
          { label: "Điểm trung bình", value: avgScore, suffix: "/10", icon: TrendingUp },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div key={i}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}
              whileHover={{ y: -4 }}
              className="bg-card border rounded-2xl p-5 shadow-sm hover:shadow-lg transition-shadow"
            >
              <Icon className="size-5 mb-3" style={{ color: "var(--portal)" }} />
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{s.label}</div>
              <div className="font-display text-3xl font-semibold">
                <CountUp to={s.value} />{s.suffix}
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="bg-card border rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold">Bài tập sắp đến hạn</h3>
          <Link to="/student/assignments" className="text-xs font-medium" style={{ color: "var(--portal)" }}>Xem tất cả →</Link>
        </div>
        {due.length ? (
          <ul className="space-y-2">
            {due.map((d, i) => (
              <li key={i} className={`flex items-center gap-3 p-3 rounded-xl ${d.bg}`}>
                <d.Icon className={`size-5 shrink-0 ${d.color}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{d.title}</div>
                  <div className="text-xs text-muted-foreground">{d.time}</div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-6">
            <CheckCircle2 className="size-8 mx-auto mb-2 text-emerald-500" />
            <p className="text-sm text-muted-foreground">Bạn đã hoàn thành tất cả bài tập đến hạn</p>
          </div>
        )}
      </motion.div>
    </PortalLayout>
  );
}
